import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Camera, Loader2, ScanLine, Swords, Sparkles, RotateCcw } from "lucide-react";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const SKILLS = ["acrobatics", "arcana", "athletics", "crafting", "deception", "diplomacy", "intimidation", "medicine", "nature", "occultism", "performance", "religion", "society", "stealth", "survival", "thievery"];
const RANKS = ["untrained", "trained", "expert", "master", "legendary"];
const DND_SKILLS = ["acrobatics", "animal_handling", "arcana", "athletics", "deception", "history", "insight", "intimidation", "investigation", "medicine", "nature", "perception", "performance", "persuasion", "religion", "sleight_of_hand", "stealth", "survival"];

/**
 * Shared "Photo-to-Sheet" OCR flow: upload a photo of a paper PF2e or D&D 5e sheet, run it
 * through the backend vision-LLM endpoint (which reads the game's system and adapts its prompt),
 * then let the user review/edit every field before it's applied.
 * Used both for brand-new character creation and for re-syncing an existing sheet.
 */
export default function OcrImportModal({ open, onOpenChange, gameId, isHomebrew, isDnd5e, onConfirm }) {
  const [step, setStep] = useState("upload"); // upload | loading | review
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [ancestries, setAncestries] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [classes, setClasses] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("upload");
      setPreview(null);
      setDraft(null);
      return;
    }
    if (!isHomebrew && !isDnd5e) {
      api.get("/pf2e/ancestries").then((r) => setAncestries(r.data));
      api.get("/pf2e/backgrounds").then((r) => setBackgrounds(r.data));
      api.get("/pf2e/classes").then((r) => setClasses(r.data));
    }
  }, [open, isHomebrew, isDnd5e]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      return toast.error("Please choose a JPEG, PNG or WEBP photo");
    }
    setPreview(URL.createObjectURL(file));
    setStep("loading");
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (gameId) formData.append("game_id", gameId);
      const { data } = await api.post("/characters/ocr", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(data);
      setStep("review");
      if ((data.unmatched_weapons?.length || data.unmatched_spells?.length)) {
        toast("Some items couldn't be auto-matched — add them manually after import.", { icon: <Sparkles className="w-4 h-4" /> });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't read that photo. Try again with better lighting.");
      setStep("upload");
    }
  };

  const patchDraft = (updates) => setDraft((d) => ({ ...d, ...updates }));
  const patchDnd = (updates) => setDraft((d) => ({ ...d, dnd5e: { ...d.dnd5e, ...updates } }));

  const confirm = () => {
    if (!draft.name?.trim()) return toast.error("Give your character a name");
    onConfirm(draft);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-lg max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="ocr-import-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#D4AF37]" /> Import Paper Sheet
          </DialogTitle>
          <DialogDescription>
            {step === "upload" && "Snap or upload a photo of your paper character sheet — we'll read it and fill in a draft for you to check."}
            {step === "loading" && "Reading your sheet…"}
            {step === "review" && "Double-check everything below, then import. Nothing is saved until you confirm."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              data-testid="ocr-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              data-testid="ocr-upload-dropzone"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-white/15 hover:border-[#D4AF37]/50 rounded-2xl py-12 flex flex-col items-center gap-3 text-[#f1e0c5]/50 transition-colors duration-200"
            >
              <Camera className="w-8 h-8" />
              <span className="text-sm">Tap to take a photo or choose an image</span>
            </button>
          </div>
        )}

        {step === "loading" && (
          <div className="flex flex-col items-center gap-4 py-10" data-testid="ocr-loading-state">
            {preview && <img src={preview} alt="Sheet preview" className="w-28 h-28 object-cover rounded-xl border border-white/10 opacity-70" />}
            <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
            <p className="text-sm text-[#f1e0c5]/50">Transcribing your sheet with vision AI…</p>
          </div>
        )}

        {step === "review" && draft && isDnd5e && (
          <div className="space-y-4 pt-1" data-testid="ocr-review-dnd5e">
            <Input data-testid="ocr-name-input" placeholder="Character name" value={draft.name} onChange={(e) => patchDraft({ name: e.target.value })} className="bg-black/30 border-white/10" />

            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="ocr-dnd-race-input" placeholder="Race" value={draft.dnd5e?.race || ""} onChange={(e) => patchDnd({ race: e.target.value })} className="bg-black/30 border-white/10" />
              <Input data-testid="ocr-dnd-class-input" placeholder="Class" value={draft.dnd5e?.class_name || ""} onChange={(e) => patchDnd({ class_name: e.target.value })} className="bg-black/30 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="ocr-dnd-background-input" placeholder="Background" value={draft.dnd5e?.background || ""} onChange={(e) => patchDnd({ background: e.target.value })} className="bg-black/30 border-white/10" />
              <Input data-testid="ocr-dnd-alignment-input" placeholder="Alignment" value={draft.dnd5e?.alignment || ""} onChange={(e) => patchDnd({ alignment: e.target.value })} className="bg-black/30 border-white/10" />
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0">Level</p>
              <Input data-testid="ocr-level-input" type="number" min={1} max={20} value={draft.level} onChange={(e) => patchDraft({ level: parseInt(e.target.value, 10) || 1 })} className="bg-black/30 border-white/10 w-16" />
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0">AC</p>
              <Input data-testid="ocr-dnd-ac-input" type="number" value={draft.dnd5e?.ac ?? ""} onChange={(e) => patchDnd({ ac: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="10" className="bg-black/30 border-white/10 w-16" />
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0">Speed</p>
              <Input data-testid="ocr-dnd-speed-input" type="number" value={draft.dnd5e?.speed ?? ""} onChange={(e) => patchDnd({ speed: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="30" className="bg-black/30 border-white/10 w-16" />
            </div>
            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0">HP</p>
              <Input data-testid="ocr-hp-current-input" type="number" value={draft.hp_current ?? ""} onChange={(e) => patchDraft({ hp_current: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="cur" className="bg-black/30 border-white/10 w-16" />
              <span className="text-[#f1e0c5]/30">/</span>
              <Input data-testid="ocr-hp-max-input" type="number" value={draft.hp_max ?? ""} onChange={(e) => patchDraft({ hp_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="max" className="bg-black/30 border-white/10 w-16" />
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0 ml-2">Hit Dice</p>
              <Input data-testid="ocr-dnd-hit-dice-input" placeholder="3d8" value={draft.dnd5e?.hit_dice || ""} onChange={(e) => patchDnd({ hit_dice: e.target.value })} className="bg-black/30 border-white/10 w-20" />
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Ability Scores</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {ABILITIES.map((a) => (
                  <div key={a}>
                    <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
                    <Input
                      data-testid={`ocr-ability-${a}`}
                      type="number"
                      min={1}
                      max={30}
                      value={draft.abilities[a]}
                      onChange={(e) => patchDraft({ abilities: { ...draft.abilities, [a]: parseInt(e.target.value, 10) || 10 } })}
                      className="bg-black/30 border-white/10"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Saving Throw Proficiencies</p>
              <div className="grid grid-cols-3 gap-1.5">
                {ABILITIES.map((a) => (
                  <label key={a} className="flex items-center gap-1.5 bg-black/20 rounded-lg px-2 py-1 text-xs capitalize cursor-pointer">
                    <input data-testid={`ocr-dnd-save-${a}`} type="checkbox" checked={!!draft.dnd5e?.save_proficiencies?.[a]} onChange={(e) => patchDnd({ save_proficiencies: { ...draft.dnd5e?.save_proficiencies, [a]: e.target.checked } })} className="accent-[#4d7c0f]" />
                    {a}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Proficient Skills</p>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto scrollbar-thin pr-1">
                {DND_SKILLS.map((s) => (
                  <label key={s} className="flex items-center gap-1.5 bg-black/20 rounded-lg px-2 py-1 text-xs capitalize cursor-pointer truncate">
                    <input data-testid={`ocr-dnd-skill-${s}`} type="checkbox" checked={draft.skill_proficiencies?.[s] === "proficient"} onChange={(e) => patchDraft({ skill_proficiencies: { ...draft.skill_proficiencies, [s]: e.target.checked ? "proficient" : "none" } })} className="accent-[#4d7c0f]" />
                    {s.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-5 gap-2">
              {["cp", "sp", "ep", "gp", "pp"].map((c) => (
                <div key={c}>
                  <label className="text-[10px] uppercase text-[#D4AF37]">{c}</label>
                  <Input data-testid={`ocr-currency-${c}`} type="number" value={draft.currency[c]} onChange={(e) => patchDraft({ currency: { ...draft.currency, [c]: parseInt(e.target.value, 10) || 0 } })} className="bg-black/30 border-white/10" />
                </div>
              ))}
            </div>

            {draft.dnd5e?.attacks?.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-[#4d7c0f]" data-testid="ocr-dnd-matched-attacks">
                <Swords className="w-3.5 h-3.5 shrink-0" /> Read {draft.dnd5e.attacks.length} attack/spellcasting row(s) — check the sheet's Attacks tab after import.
              </div>
            )}

            <Textarea data-testid="ocr-dnd-features-text" placeholder="Features & Traits" value={draft.dnd5e?.features_text || ""} onChange={(e) => patchDnd({ features_text: e.target.value })} className="bg-black/30 border-white/10 text-xs h-20" />
            <Textarea data-testid="ocr-dnd-equipment-text" placeholder="Equipment" value={draft.dnd5e?.equipment_text || ""} onChange={(e) => patchDnd({ equipment_text: e.target.value })} className="bg-black/30 border-white/10 text-xs h-20" />
            <Textarea data-testid="ocr-dnd-other-profs" placeholder="Other Proficiencies & Languages" value={draft.dnd5e?.other_profs_languages || ""} onChange={(e) => patchDnd({ other_profs_languages: e.target.value })} className="bg-black/30 border-white/10 text-xs h-16" />

            {draft.notes && <p className="text-xs text-[#f1e0c5]/40 italic" data-testid="ocr-llm-notes">Note: {draft.notes}</p>}

            <div className="flex gap-2 pt-1">
              <Button data-testid="ocr-retake-button" variant="secondary" onClick={() => setStep("upload")} className="rounded-full gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Retake
              </Button>
              <Button data-testid="ocr-confirm-import-button" onClick={confirm} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                Looks Good — Import
              </Button>
            </div>
          </div>
        )}

        {step === "review" && draft && !isDnd5e && (
          <div className="space-y-4 pt-1" data-testid="ocr-review-pf2e">
            <Input data-testid="ocr-name-input" placeholder="Character name" value={draft.name} onChange={(e) => patchDraft({ name: e.target.value })} className="bg-black/30 border-white/10" />

            {!isHomebrew && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={draft.ancestry_slug || "none"} onValueChange={(v) => patchDraft({ ancestry_slug: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="ocr-ancestry-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Ancestry" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— none —</SelectItem>{ancestries.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={draft.class_slug || "none"} onValueChange={(v) => patchDraft({ class_slug: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="ocr-class-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Class" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— none —</SelectItem>{classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Select value={draft.background_slug || "none"} onValueChange={(v) => patchDraft({ background_slug: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="ocr-background-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Background" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— none —</SelectItem>{backgrounds.map((b) => <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}

            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0">Level</p>
              <Input data-testid="ocr-level-input" type="number" min={1} max={20} value={draft.level} onChange={(e) => patchDraft({ level: parseInt(e.target.value, 10) || 1 })} className="bg-black/30 border-white/10 w-20" />
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 shrink-0 ml-2">HP</p>
              <Input data-testid="ocr-hp-current-input" type="number" value={draft.hp_current ?? ""} onChange={(e) => patchDraft({ hp_current: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="cur" className="bg-black/30 border-white/10 w-16" />
              <span className="text-[#f1e0c5]/30">/</span>
              <Input data-testid="ocr-hp-max-input" type="number" value={draft.hp_max ?? ""} onChange={(e) => patchDraft({ hp_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })} placeholder="max" className="bg-black/30 border-white/10 w-16" />
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Ability Scores</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {ABILITIES.map((a) => (
                  <div key={a}>
                    <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
                    <Input
                      data-testid={`ocr-ability-${a}`}
                      type="number"
                      min={1}
                      max={30}
                      value={draft.abilities[a]}
                      onChange={(e) => patchDraft({ abilities: { ...draft.abilities, [a]: parseInt(e.target.value, 10) || 10 } })}
                      className="bg-black/30 border-white/10"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Trained Skills</p>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto scrollbar-thin pr-1">
                {SKILLS.map((s) => (
                  <div key={s} className="flex items-center justify-between bg-black/20 rounded-lg px-2 py-1 gap-1">
                    <span className="text-xs capitalize truncate">{s}</span>
                    <Select value={draft.skill_proficiencies?.[s] || "untrained"} onValueChange={(v) => patchDraft({ skill_proficiencies: { ...draft.skill_proficiencies, [s]: v } })}>
                      <SelectTrigger data-testid={`ocr-skill-${s}`} className="h-6 w-24 bg-black/30 border-white/10 text-[10px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {["cp", "sp", "gp", "pp"].map((c) => (
                <div key={c}>
                  <label className="text-[10px] uppercase text-[#D4AF37]">{c}</label>
                  <Input data-testid={`ocr-currency-${c}`} type="number" value={draft.currency[c]} onChange={(e) => patchDraft({ currency: { ...draft.currency, [c]: parseInt(e.target.value, 10) || 0 } })} className="bg-black/30 border-white/10" />
                </div>
              ))}
            </div>

            <Input data-testid="ocr-languages-input" placeholder="Languages (comma separated)" value={(draft.languages || []).join(", ")} onChange={(e) => patchDraft({ languages: e.target.value.split(",").map((l) => l.trim()).filter(Boolean) })} className="bg-black/30 border-white/10 h-8 text-xs" />
            <Input data-testid="ocr-heritage-input" placeholder="Heritage and traits" value={draft.heritage_text || ""} onChange={(e) => patchDraft({ heritage_text: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />

            {(draft.inventory?.length > 0 || draft.known_spell_slugs?.length > 0) && (
              <div className="flex items-center gap-2 flex-wrap text-xs text-[#4d7c0f]" data-testid="ocr-matched-items">
                <Swords className="w-3.5 h-3.5 shrink-0" />
                Matched {draft.inventory?.length || 0} weapon(s) and {draft.known_spell_slugs?.length || 0} spell(s) — will be added to your sheet.
              </div>
            )}
            {(draft.unmatched_weapons?.length > 0 || draft.unmatched_spells?.length > 0) && (
              <div className="text-xs text-[#D4AF37]/80 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg px-3 py-2" data-testid="ocr-unmatched-warning">
                Couldn't confidently match: {[...(draft.unmatched_weapons || []), ...(draft.unmatched_spells || [])].join(", ")} — add these manually after import.
              </div>
            )}
            {draft.notes && <p className="text-xs text-[#f1e0c5]/40 italic" data-testid="ocr-llm-notes">Note: {draft.notes}</p>}

            <div className="flex gap-2 pt-1">
              <Button data-testid="ocr-retake-button" variant="secondary" onClick={() => setStep("upload")} className="rounded-full gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Retake
              </Button>
              <Button data-testid="ocr-confirm-import-button" onClick={confirm} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                Looks Good — Import
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
